/**
 * UNIFIED PLAYER CORE STORE
 * 
 * This Zustand store is the SINGLE SOURCE OF TRUTH for core player state:
 * - Player identity (playerId, playerName, groupId)
 * - Session token (for RLS)
 * - Current time period
 * - Financial status (money, earned, spent)
 * - Player age (derived from period)
 * 
 * Other hooks should consume this store instead of making their own Supabase queries
 * for this common data.
 */

import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { TimeProgressionService } from '@/services/timeProgressionService';
import SessionTokenManager from '@/services/sessionTokenManager';
// CRITICAL: Use lightweight cache instead of freeworldBootstrapStore
// This prevents FreeWorld data from loading on /module/* routes
import { loadTimePeriodsLightweight, getCachedTimePeriodsLightweight } from '@/lib/timePeriodsCache';
import type { TimePeriod, PlayerFinancialStatus } from '@/types/timeProgression';

export interface PlayerCoreState {
  // Identity
  playerId: string | null;
  playerName: string | null;
  groupId: string | null;
  
  // Session
  sessionToken: string | null;
  
  // Era & age
  currentPeriod: TimePeriod | null;
  playerAge: number | null;
  
  // Financial
  financialStatus: PlayerFinancialStatus | null;
  
  // State flags
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
  
  // Internal flag to prevent double initialization
  _initPromise: Promise<void> | null;
}

export interface PlayerCoreActions {
  // Initialize with player session data
  initialize: (playerId: string, playerName: string, groupId: string, sessionToken: string) => Promise<void>;
  
  // Refresh all core data
  refresh: () => Promise<void>;
  
  // Update money (positive = earn, negative = spend)
  updateMoney: (delta: number, type: 'earned' | 'spent') => Promise<boolean>;
  
  // Set financial status directly (for external updates)
  setFinancialStatus: (status: PlayerFinancialStatus) => void;
  
  // Set current period directly (for external updates like time jump)
  setCurrentPeriod: (period: TimePeriod) => void;
  
  // Set age directly
  setPlayerAge: (age: number) => void;
  
  // Reset store (on logout)
  reset: () => void;
}

type PlayerCoreStore = PlayerCoreState & PlayerCoreActions;

// Helper to derive age from period name (e.g., "18 vuotta" -> 18)
const deriveAgeFromPeriod = (periodName: string): number => {
  const match = periodName.match(/^(\d+)\s*vuotta/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  return 15; // Default starting age
};

export const usePlayerCoreStore = create<PlayerCoreStore>((set, get) => ({
  // Initial state
  playerId: null,
  playerName: null,
  groupId: null,
  sessionToken: null,
  currentPeriod: null,
  playerAge: null,
  financialStatus: null,
  isLoading: false,
  isReady: false,
  error: null,
  _initPromise: null,

  initialize: async (playerId, playerName, groupId, sessionToken) => {
    const state = get();
    
    // If already initialized with same player, just refresh
    if (state.isReady && state.playerId === playerId && state.sessionToken === sessionToken) {
      return;
    }
    
    // If currently initializing, wait for that to complete
    if (state._initPromise) {
      await state._initPromise;
      return;
    }
    
    // Create initialization promise
    const initPromise = (async () => {
      set({ 
        isLoading: true, 
        error: null,
        playerId,
        playerName,
        groupId,
        sessionToken
      });

      try {
        // Ensure session token is set for RLS
        await SessionTokenManager.ensureTokenSet(sessionToken);

        // Get current time period using existing service
        const systemPeriod = await TimeProgressionService.getCurrentTimePeriod(playerId, sessionToken);
        
        if (!systemPeriod) {
          throw new Error('Failed to load current time period');
        }

        // Get or create financial status
        const financialData = await TimeProgressionService.getOrCreateFinancialStatus(
          playerId,
          systemPeriod,
          sessionToken
        );

        if (!financialData) {
          throw new Error('Failed to load financial status');
        }

        // Determine actual current period from financial status
        let currentPeriod = systemPeriod;
        if (financialData.current_time_period_id && financialData.current_time_period_id !== systemPeriod.id) {
          // Use cached time periods instead of separate query
          const cachedPeriods = getCachedTimePeriodsLightweight();
          if (cachedPeriods) {
            const periodData = cachedPeriods.find(p => p.id === financialData.current_time_period_id);
            if (periodData) {
              currentPeriod = periodData;
            }
          } else {
            // Fallback: load from store if cache miss
            const periods = await loadTimePeriodsLightweight({ reason: 'PlayerCoreStore.initialize' });
            const periodData = periods.find(p => p.id === financialData.current_time_period_id);
            if (periodData) {
              currentPeriod = periodData;
            }
          }
        }

        // Derive age from period name
        const age = deriveAgeFromPeriod(currentPeriod.period_name);

        set({
          currentPeriod,
          financialStatus: financialData,
          playerAge: age,
          isLoading: false,
          isReady: true,
          error: null,
          _initPromise: null
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize player core';
        console.error('[PlayerCore] Initialization error:', errorMessage);
        set({
          isLoading: false,
          isReady: false,
          error: errorMessage,
          _initPromise: null
        });
      }
    })();

    set({ _initPromise: initPromise });
    await initPromise;
  },

  refresh: async () => {
    const state = get();
    
    if (!state.playerId || !state.sessionToken) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Invalidate cache first
      TimeProgressionService.invalidateFinancialStatusCache(state.playerId);

      // Get current period
      const systemPeriod = await TimeProgressionService.getCurrentTimePeriod(state.playerId, state.sessionToken);
      
      if (!systemPeriod) {
        throw new Error('Failed to refresh time period');
      }

      // Get financial status
      const financialData = await TimeProgressionService.getOrCreateFinancialStatus(
        state.playerId,
        systemPeriod,
        state.sessionToken
      );

      if (!financialData) {
        throw new Error('Failed to refresh financial status');
      }

      // Determine actual current period
      let currentPeriod = systemPeriod;
      if (financialData.current_time_period_id && financialData.current_time_period_id !== systemPeriod.id) {
        // Use cached time periods instead of separate query
        const cachedPeriods = getCachedTimePeriodsLightweight();
        if (cachedPeriods) {
          const periodData = cachedPeriods.find(p => p.id === financialData.current_time_period_id);
          if (periodData) {
            currentPeriod = periodData;
          }
        } else {
          // Fallback: load from store if cache miss
          const periods = await loadTimePeriodsLightweight({ reason: 'PlayerCoreStore.refresh' });
          const periodData = periods.find(p => p.id === financialData.current_time_period_id);
          if (periodData) {
            currentPeriod = periodData;
          }
        }
      }

      const age = deriveAgeFromPeriod(currentPeriod.period_name);

      set({
        currentPeriod,
        financialStatus: financialData,
        playerAge: age,
        isLoading: false,
        error: null
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh player core';
      console.error('[PlayerCore] Refresh error:', errorMessage);
      set({
        isLoading: false,
        error: errorMessage
      });
    }
  },

  updateMoney: async (delta, type) => {
    const state = get();
    
    if (!state.playerId || !state.sessionToken || !state.financialStatus) {
      console.warn('[PlayerCore] updateMoney blocked: missing required state');
      return false;
    }

    const newMoney = state.financialStatus.current_money + delta;
    
    // Check for negative money on spend
    if (newMoney < 0 && type === 'spent') {
      return false;
    }

    const updates: Partial<PlayerFinancialStatus> = {
      current_money: newMoney,
      ...(type === 'earned'
        ? { total_earned: state.financialStatus.total_earned + Math.abs(delta) }
        : { total_spent: state.financialStatus.total_spent + Math.abs(delta) }
      )
    };

    try {
      const updatedStatus = await TimeProgressionService.updateFinancialStatus(
        state.playerId,
        updates,
        state.sessionToken
      );

      if (updatedStatus) {
        set({ financialStatus: updatedStatus });
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('[PlayerCore] updateMoney error:', err);
      return false;
    }
  },

  setFinancialStatus: (status) => {
    set({ financialStatus: status });
  },

  setCurrentPeriod: (period) => {
    const age = deriveAgeFromPeriod(period.period_name);
    set({ currentPeriod: period, playerAge: age });
  },

  setPlayerAge: (age) => {
    set({ playerAge: age });
  },

  reset: () => {
    set({
      playerId: null,
      playerName: null,
      groupId: null,
      sessionToken: null,
      currentPeriod: null,
      playerAge: null,
      financialStatus: null,
      isLoading: false,
      isReady: false,
      error: null,
      _initPromise: null
    });
  }
}));

// Convenience hook for components that need player core data
export const usePlayerCore = () => usePlayerCoreStore();
